import {
  Box,
  CollapsibleCard,
  ConfirmationDialogBox,
  IconButton,
} from '@atgfr8/atg-ui-tailwind';
import { AccountIcon, CheckIcon, DeleteIcon, PencilIcon } from '@atgfr8/icons';
import { AutoComplete, BasicModal, ToastType } from '@components';
import { CarrierCSPAccount, DEFAULT_COUNTRY_SUPPORT } from '@store/carriers';
import {
  CarrierScac,
  customerLTLSettings,
  deleteExistingScacAccount,
  EvaAccessIdRequirement,
  getBillToName,
  getCspCarrierOptions,
  getCustomerSpecificPricingData,
  getEvaAccessIdRequirements,
  getScacAccountTypes,
  ScacAccount,
} from '@store/customers/customerDetails/LTL';
import { closeModal, isModalOpen, openModal } from '@store/modals';
import { handleToast } from '@utils/utils';
import LTLPricingSetupEditModal from '@views/carriers/carrierDetails/carrierLTLTab/components/LTLPricingSetupEditModal';
import { size } from 'lodash';
import { createEffect, createSignal, For, Show } from 'solid-js';

import { CustomerSpecificPricing as classes } from './classes';

const CustomerSpecificPricing = (props: { customerId: number }) => {
  const [selectedAccount, setSelectedAccount] =
    createSignal<ScacAccount | null>(null);
  const [chosenCarrier, setChosenCarrier] = createSignal<CarrierScac>();
  const [evaAccessIdRequirements, setEvaAccessIdRequirements] = createSignal<
    EvaAccessIdRequirement | undefined
  >();
  const [billToName, setBillToName] = createSignal<string | undefined>();
  const [scacAccountToDelete, setScacAccountToDelete] = createSignal<
    number | null
  >(null);
  const [getIsDialogOpen, setIsDialogOpen] = createSignal(false);

  createEffect(async () => {
    if (props.customerId) {
      await Promise.all([
        getCustomerSpecificPricingData(props.customerId),
        getCspCarrierOptions(props.customerId),
        getScacAccountTypes(),
      ]);
    }
  });

  const ltlScacAccounts = () => customerLTLSettings.cspSetup.ltlScacAccounts;
  const ltlCspCarrierOptions = () =>
    customerLTLSettings.cspSetup.ltlCspCarrierOptions;

  const handleEdit = async (account: ScacAccount) => {
    setSelectedAccount(account);
    setChosenCarrier({
      carrierId: account.carrierId!,
      carrierName: account.carrierName!,
      carrierScacId: account.carrierScacId!,
      scac: account.scac!,
    });
    const evaAccessIdRequirements = await getEvaAccessIdRequirements(
      props.customerId,
      account.carrierScacId!,
    );
    setEvaAccessIdRequirements(evaAccessIdRequirements);
    openModal('csp-setup-modal');
  };

  const handleDelete = async () => {
    if (scacAccountToDelete() != null) {
      await deleteExistingScacAccount(scacAccountToDelete()!);
      setScacAccountToDelete(null);
      await getCustomerSpecificPricingData(props.customerId);
      handleToast(ToastType.Success, 'SCAC Account Successfully Deleted');
    }
  };

  const onDelete = (scacAccountId: number) => {
    setScacAccountToDelete(scacAccountId);
    setIsDialogOpen(true);
  };

  const onItemSelect = async (item: CarrierScac) => {
    const billToNameCreate = await getBillToName(
      props.customerId,
      item.carrierScacId,
    );
    const evaAccessIdRequirements = await getEvaAccessIdRequirements(
      props.customerId,
      item.carrierScacId,
    );
    setEvaAccessIdRequirements(evaAccessIdRequirements);
    setBillToName(billToNameCreate);
    setChosenCarrier(item);
    setSelectedAccount(null);

    openModal('csp-setup-modal');
  };

  const handleModalClose = () => {
    setSelectedAccount(null);
    setChosenCarrier(undefined);
    setEvaAccessIdRequirements(undefined);
    setBillToName(undefined);
    closeModal('csp-setup-modal');
  };

  const handleModalSave = async () => {
    setSelectedAccount(null);
    await getCustomerSpecificPricingData(props.customerId);
  };

  return (
    <CollapsibleCard
      startTitle="Customer Specific Pricing"
      loading={customerLTLSettings.isLoading}
      class="mb-2"
    >
      <Box class={classes.container}>
        Find a carrier to set up Customer Specific Pricing with. If you do not
        see the carrier you are looking for, please contact IT helpdesk to set
        the carrier up for LTL Quoting.
      </Box>

      <AutoComplete
        id="autocomplete-csp-carrier-options-search"
        endpoint=""
        isStatic={true}
        fetchData={(searchQuery, setResults) => {
          const allCarriers = ltlCspCarrierOptions();
          if (!searchQuery) {
            setResults(allCarriers);
            return;
          }
          const lower = searchQuery.toLowerCase();
          const filtered = allCarriers.filter((carrier) =>
            carrier.carrierName.toLowerCase().includes(lower),
          );
          setResults(filtered);
        }}
        onItemSelect={(item) => onItemSelect(item as CarrierScac)}
        multipleValueRender={() => {
          return (
            <For each={ltlScacAccounts()}>
              {(item) => (
                <Box class="flex mt-1 justify-between p-2 border-2 shadow-xs">
                  <Box class="flex gap-8 items-center">
                    <AccountIcon class="text-[#026EA1]" />
                    <Box class="font-medium text-base">
                      {item.carrierName}
                      <Box class={classes.carrierDetailsText}>
                        SCAC:{' '}
                        <span class="text-sm! font-normal">{item.scac}</span>
                        {' | '}
                        Account Number:{' '}
                        <span class="text-sm! font-normal">
                          {item.accountNumber}
                        </span>
                        {' | '}
                        Term Type:{' '}
                        <span class="text-sm! font-normal">
                          {item.termTypeDescription}
                        </span>
                        {' | '}
                        Payer Type:{' '}
                        <span class="text-sm! font-normal">
                          {item.payerTypeDescription}
                        </span>
                      </Box>
                    </Box>
                  </Box>
                  <Box class="flex flex-col items-center justify-center w-14">
                    <IconButton
                      onClick={() => handleEdit(item)}
                      dataTestId="customer-specific-pricing-edit-icon-button"
                    >
                      <PencilIcon class="text-[22px]! text-blue-700" />
                    </IconButton>
                  </Box>
                  <Box class="flex flex-col items-center justify-center w-14">
                    <IconButton
                      onClick={() => onDelete(item.id!)}
                      dataTestId="customer-specific-pricing-delete-icon-button"
                    >
                      <DeleteIcon class="text-[22px]! text-red-700" />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </For>
          );
        }}
        renderMultipleKey="carrierName"
        mode="multiple"
        defaultValue=""
        label=""
        placeholder="Find a Carrier ..."
        version="v3"
        renderItem={(item) => {
          const carrierOption = item as CarrierScac;
          return (
            <Box class="flex gap-1">
              <AccountIcon class="text-[#026EA1]" />
              <Box class="font-medium text-base">
                {carrierOption.carrierName}

                <Box class={classes.carrierDetailsText}>
                  SCAC:{' '}
                  <span class="text-sm! font-normal text-[#000000]">
                    {carrierOption.scac}
                  </span>{' '}
                </Box>
              </Box>
              <Box class="flex flex-col w-14">
                <span class={classes.carrierLTLBox}>
                  LTL
                  <CheckIcon class="text-[13px]! text-white" />
                </span>
              </Box>
            </Box>
          );
        }}
      />
      <Show when={size(ltlScacAccounts()) === 0}>
        <Box class={classes.noCarrierContainer}>
          <Box class={classes.noCarrierText}>
            No carriers have been set up for Customer Specific Pricing for this
            customer
          </Box>
        </Box>
      </Show>
      <BasicModal
        id="csp-setup-modal"
        title="Customer Specific Pricing Setup"
        width="80vw"
        onClose={handleModalClose}
        footer={false}
        showClose
      >
        {/* Remount on each open so onMount-driven form init doesn't reuse stale state across edits */}
        <Show when={isModalOpen('csp-setup-modal')}>
          <LTLPricingSetupEditModal
            modalId="csp-setup-modal"
            onModalClose={handleModalClose}
            scacs={
              chosenCarrier()
                ? [
                    {
                      ...chosenCarrier()!,
                      // Padding out CarrierScac for the SCAC dropdown, which only reads
                      // scac/carrierScacId/carrierId/carrierName. Country eligibility is
                      // edited on the carrier's LTL tab, not here.
                      trackingUrl: null,
                      ...DEFAULT_COUNTRY_SUPPORT,
                    },
                  ]
                : []
            }
            evaRequirements={evaAccessIdRequirements()}
            selectedAccount={
              (selectedAccount() ?? undefined) as unknown as
                | CarrierCSPAccount
                | undefined
            }
            billToName={billToName()}
            customerId={props.customerId}
            onSave={handleModalSave}
          />
        </Show>
      </BasicModal>
      <ConfirmationDialogBox
        dataTestId="customerSpecificPricingDeleteDialog"
        id="customerSpecificPricingDeleteDialog"
        title="Are you sure you want to delete this Customer Specific Pricing Account?"
        onSubmit={handleDelete}
        onSubmitText="Delete"
        onCancel={() => setScacAccountToDelete(null)}
        isDialogOpen={getIsDialogOpen()}
        closeDialogBox={(_reason) => setIsDialogOpen(false)}
      />
    </CollapsibleCard>
  );
};

export default CustomerSpecificPricing;
